import React from 'react';
import { css } from 'styled-components';
import CustomMedia from 'util/custom-media-query';
import { toLocalString } from 'util/filters';
import { applyStyles, downloadFile } from 'util/helpers';
import Colors from 'constants/colors';
import FontStyles from 'constants/font-styles';
import Image from 'components/common/Image';
import giftImg1x from 'assets/images/PC/@1x/img_gift.png';
import giftImg2x from 'assets/images/PC/@2x/img_gift@2x.png';
import icBtnArrow21x from 'assets/images/common/@1x/ic_btn_arrow2.png';
import icBtnArrow22x from 'assets/images/common/@2x/ic_btn_arrow2@2x.png';
import icBtnArrow2SP2x from 'assets/images/SP/@2x/ic_btn_arrow2_sp@2x.png';
import icBtnArrow2SP3x from 'assets/images/SP/@3x/ic_btn_arrow2_sp@3x.png';
import i18n from 'i18n';
import PublishDialog from './PublishDialog';

const PriceInfo = ({ price, count }) => (
  <React.Fragment>
    <span className="yen">¥</span>
    {toLocalString(price)}
    {count && (
      <React.Fragment>
        <span className="times">×</span>
        {count}
      </React.Fragment>
    )}
  </React.Fragment>
);

const getTotalPrice = (giftCard, shippingCost) => {
  return (
    giftCard.price * giftCard.count + shippingCost.price * shippingCost.count
  );
};

const calcTotalPriceFontSize = ({ cost: { giftCard, shippingCost } }) => {
  const overflowLimitDigit =
    getTotalPrice(giftCard, shippingCost).toString().length - 6;
  return 16 - Math.max(overflowLimitDigit, 0) * 3;
};

class OrderAggregatorsTotal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      downloading: false
    };
    this.publishDialogRef = React.createRef();
  }

  handleDownloadQuotation = (voucherNum, deliveryNum, value) => {
    if (this.state.downloading) {
      return;
    }

    if (
      navigator.userAgent.includes('iPad') ||
      navigator.userAgent.includes('iPhone')
    ) {
      return window.open(
        this.props.history.createHref({
          pathname: '/preview',
          search: `?target=quotation&voucherNum=${voucherNum}&deliveryNum=${deliveryNum}&name=${value.name}`
        })
      );
    }

    this.setState({ downloading: true }, () => {
      this.props
        .downloadQuotation(voucherNum, deliveryNum, value.name)
        .then(() => {
          downloadFile('quotation.pdf', this.props.url);
          this.setState({ downloading: false });
        })
        .catch(() => {
          if (this.props.error) {
            const {
              showAlert,
              error: { data }
            } = this.props;
            const reader = new FileReader();
            reader.onload = () => {
              return showAlert(JSON.parse(reader.result).message);
            };
            reader.readAsText(data);
          }
          this.setState({ downloading: false });
        });
    });
  };

  render() {
    const {
      className,
      displayQuotation,
      cost: { giftCard, shippingCost }
    } = this.props;

    return (
      <div className={className}>
        <Image
          className="gift-img"
          pc1x={giftImg1x}
          pc2x={giftImg2x}
          alt="img_gift"
        />
        <ul className="total-ul detail-price-info">
          <li>
            {i18n.t('orderAggregators:common:total.vouchersPrice')}
            <div className="price">
              <PriceInfo price={giftCard.price} count={giftCard.count} />
            </div>
          </li>
          <li>
            {i18n.t('orderAggregators:common:total.deliveryPrice')}
            <div className="price">
              <PriceInfo
                price={shippingCost.price}
                count={shippingCost.count}
              />
            </div>
          </li>
        </ul>
        <ul className="total-ul total-price-info">
          <li>
            {i18n.t('orderAggregators:common:total.totalPrice')}
            <div className="price">
              <PriceInfo price={getTotalPrice(giftCard, shippingCost)} />
            </div>
          </li>
          {displayQuotation && (
            <li className="quotation">
              <button
                onClick={() => this.publishDialogRef.current.open()}
              >
                {i18n.t('orderAggregators:common:total.createQuotation')}
                <Image
                  pc1x={icBtnArrow21x}
                  pc2x={icBtnArrow22x}
                  sp2x={icBtnArrow2SP2x}
                  sp3x={icBtnArrow2SP3x}
                  alt="ic_btn_arrow2"
                />
              </button>
            </li>
          )}
        </ul>
        <PublishDialog
          dialogRef={this.publishDialogRef}
          onSubmit={v => this.handleDownloadQuotation(giftCard.count, shippingCost.count, v)}
          renderType={'quotation'}
        />
      </div>
    );
  }
}

const priceInfoStyle = css`
  display: flex;
  position: relative;

  .price {
    position: absolute;
    top: 0;
    right: 0;

    .yen {
      display: inline-block;
      margin-right: 4px;
    }

    .times {
      display: inline-block;
      margin: 0 4px;
    }
  }
`;

const commonStyle = css`
  width: 100%;
  background-color: ${Colors.white1};

  ul {
    ${FontStyles.text8};
    text-align: left;
    padding-left: 0;
    margin: 0;

    li {
      list-style: none;
      ${priceInfoStyle};

      .yen {
        ${FontStyles.text8};
      }
    }
  }

  .total-price-info {
    ${FontStyles.text7};

    .yen {
      ${FontStyles.text7};
    }

    .quotation {
      display: inline-block;
      width: 100%;
      text-align: right;

      button {
        cursor: pointer;
        padding: 0;
        border: none;
        outline: none;
        background-color: transparent;
      }
    }
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  height: auto;
  text-align: center;
  padding: 24px;
  box-shadow: 0px 0px 10px 0px rgba(177,177,177,0.5);
  margin-bottom: 24px;

  .gift-img {
    margin-bottom: 20px;
  }

  .detail-price-info {
    border-bottom: 1px solid ${Colors.gray2};

    li {
      margin-bottom: 12px;

      &:first-of-type {
        margin-bottom: 8px;
      }

      .price {
        ${FontStyles.text6};
        line-height: 23px !important;
      }
    }
  }

  .total-price-info {
    margin-top: 12px;

    .price {
      ${FontStyles.text5};
      line-height: 23px !important;
    }

    .quotation {
      ${FontStyles.text10};
      margin-top: 24px;

      picture {
        margin-left: 8px;
      }
    }
  }
`;

const spStyle = CustomMedia.lessThan('medium')`
  position: fixed;
  bottom: 0;
  z-index: 2;
  display: flex;

  height: 64px;
  padding: 14px 16px;
  box-shadow: 0px -2px 17px -2px rgba(177,177,177,0.5);

  .gift-img {
    display: none;
  }

  ul.total-ul {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    width: 50%;

    li {
      height: 18px;

      &:first-of-type {
        margin-bottom: 8px;
      }
    }
  }

  .detail-price-info {
    ${FontStyles.text10};

    position: relative;
    padding-right: 16px;
    line-height: 18px !important

    &:after {
      position: absolute;
      right: 0;
      content: '';
      height: 48px;
      border-right: 1px solid ${Colors.gray2}
    }

    .price {
      ${FontStyles.text8};
      line-height: 18px !important

      .yen {
        ${FontStyles.text10};
        line-height: 18px !important
      }
    }
  }

  .total-price-info {
    margin-left: 16px;
    ${FontStyles.text9};
    line-height: 18px !important;

    .price {
      ${FontStyles.text5};
      line-height: 18px !important
      max-width: 90px;
      font-size: ${props => calcTotalPriceFontSize(props)}px !important;

      .yen {
        ${FontStyles.text9};
        line-height: 18px !important
      }
    }

    .quotation {
      ${FontStyles.text8};
      line-height: 18px !important
      display: flex;
      justify-content: flex-end;
      align-items: center;

      picture {
        margin-left: 6px;
        vertical-align: text-bottom;
      }
    }
  }
`;

export default applyStyles(OrderAggregatorsTotal)(
  commonStyle,
  pcStyle,
  spStyle
);



// WEBPACK FOOTER //
// ./src/components/OrderAggregators/components/Total.js